import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOrganizationsApi } from '@app/hooks/useApis';
import { useAuthSession } from '@app/context/auth-session';
import { buildLoginRedirectPath } from '@app/utils/auth-redirect';
import { resolveErrorMessage } from '@app/utils/errors';

const OrgCreatePage = () => {
  const organizationsApi = useOrganizationsApi();
  const { tokens, switchOrganization } = useAuthSession();
  const navigate = useNavigate();
  const location = useLocation();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bindingError, setBindingError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [lastCreatedOrgId, setLastCreatedOrgId] = useState<string | null>(null);
  const [lastCreatedOrgName, setLastCreatedOrgName] = useState<string | null>(null);

  useEffect(() => {
    if (!tokens) {
      navigate(buildLoginRedirectPath(location), { replace: true });
      return;
    }
  }, [location, navigate, tokens]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(buildLoginRedirectPath(location));
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();

    if (!trimmedName) {
      setError('Enter an organization name.');
      return;
    }

    setBusy(true);
    setError(null);
    setBindingError(null);
    setSuccessMessage(null);

    let createdOrgId: string | null = null;
    let createdOrgName = trimmedName;

    try {
      const created = await organizationsApi.orgsPost({
        organizationCreate: { name: trimmedName, notes: trimmedDescription || undefined }
      });
      createdOrgId = created.id ?? null;
      createdOrgName = created.name ?? trimmedName;
      setLastCreatedOrgId(createdOrgId);
      setLastCreatedOrgName(createdOrgName);
    } catch (caught) {
      const message = await resolveErrorMessage(
        caught,
        'We could not create the organization. Check the details and try again.'
      );
      setError(message);
      setBusy(false);
      return;
    }

    if (!createdOrgId) {
      setBindingError('Organization created, but your session could not switch. Sign in again to continue.');
      setBusy(false);
      return;
    }

    setSuccessMessage(`Organization created. Switching you to ${createdOrgName}…`);

    try {
      await switchOrganization(createdOrgId);
      setLastCreatedOrgId(null);
      navigate('/app', { replace: true });
    } catch (caught) {
      const message = await resolveErrorMessage(
        caught,
        'The organization was created, but we could not switch your session. Try again or sign in once more.'
      );
      setBindingError(message);
      setSuccessMessage(null);
      setBusy(false);
    }
  };

  const handleBindingRetry = async () => {
    if (!lastCreatedOrgId) {
      navigate(buildLoginRedirectPath(location));
      return;
    }

    setBusy(true);
    setBindingError(null);
    setSuccessMessage(`Switching you to ${lastCreatedOrgName ?? 'your new organization'}…`);

    try {
      await switchOrganization(lastCreatedOrgId);
      setLastCreatedOrgId(null);
      navigate('/app', { replace: true });
    } catch (caught) {
      const message = await resolveErrorMessage(
        caught,
        'We created the organization, but switching sessions failed. Try again or sign in once more.'
      );
      setBindingError(message);
      setSuccessMessage(null);
      setBusy(false);
    }
  };

  return (
    <main className="auth-experience">
      <section className="auth-experience__card">
        <header className="auth-experience__header">
          <h1>Create your organization</h1>
          <p>You&rsquo;ll become the initial administrator for this workspace.</p>
        </header>

        <div className="auth-experience__body auth-stack">
          <form className="auth-form auth-stack" onSubmit={handleSubmit} noValidate>
            <div className="auth-field-stack">
              <label htmlFor="org-name" className="auth-field-label">
                Organization name
              </label>
              <input
                id="org-name"
                name="org-name"
                type="text"
                placeholder="Acme Field Ops"
                className="auth-input"
                value={name}
                disabled={busy}
                onChange={(event) => setName(event.target.value)}
                required
              />
              <p className="auth-field-help">You can change this later in Settings.</p>
            </div>

            <div className="auth-field-stack">
              <label htmlFor="org-description" className="auth-field-label">
                Description (optional)
              </label>
              <textarea
                id="org-description"
                name="org-description"
                placeholder="Add a short description to help teammates recognize this workspace."
                className="auth-input"
                rows={3}
                value={description}
                disabled={busy}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>

            {error && (
              <p className="auth-error" role="alert">
                {error}
              </p>
            )}

            {bindingError && (
              <div className="auth-stack">
                <p className="auth-error" role="alert">
                  {bindingError}
                </p>
                <button type="button" className="auth-link-button" onClick={handleBindingRetry} disabled={busy}>
                  Try switching again
                </button>
              </div>
            )}

            {successMessage && <p className="auth-note">{successMessage}</p>}

            <div className="auth-inline-actions">
              <button type="submit" className="auth-primary" disabled={busy}>
                {busy ? 'Creating…' : 'Create organization'}
              </button>
              <button type="button" className="auth-secondary" onClick={handleBack} disabled={busy}>
                Back
              </button>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
};

export default OrgCreatePage;
